/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
document.addEventListener("DOMContentLoaded", () => {
  try {
    const scrollContainer = document.querySelector(
      ".collection-split .collection-split__products .float-grid"
    );
    const scrollbar = document.querySelector(".custom-scrollbar");
    const style = window.getComputedStyle(scrollContainer);
    const paddingLeft = parseInt(style.paddingLeft);
    const updateScrollbar = () => {
      const scrollWidth =
        scrollContainer.scrollWidth - paddingLeft - paddingLeft;
      const clientWidth =
        scrollContainer.clientWidth - paddingLeft - paddingLeft;
      const scrollLeft = scrollContainer.scrollLeft;

      const trackWidth = (clientWidth / scrollWidth) * clientWidth;
      const position = (scrollLeft / scrollWidth) * clientWidth;

      if (scrollWidth > clientWidth) {
        scrollbar.style.setProperty("--track-width", `${trackWidth}px`);
        scrollbar.style.setProperty("--position", `${position}px`);
        scrollbar.style.display = "block";
      } else {
        scrollbar.style.display = "none";
        scrollbar.style.setProperty("--track-width", `0px`);
      }
    };

    updateScrollbar();

    scrollContainer.addEventListener("scroll", updateScrollbar);

    window.addEventListener("resize", updateScrollbar);

    // set pc scroll
    const grid = document.querySelector(".collection-split__grid");
    const container = document.querySelector(
      ".collection-split__products .float-grid.grid--2"
    );
    const items = document.querySelectorAll(".collection-split__product");

    if (grid && container) {
      const gridHeight = grid.offsetHeight;

      container.style.maxHeight = `${gridHeight}px`;

      items.forEach((item) => {
        item.classList.remove("pc-hidden");
        item.style.display = "";
      });
    }
  } catch (e) {
    console.log(e);
  }
  // custom handle variant change
  window.customHandleVariantChange = function (variantData) {
    console.log(variantData, 'variantData')
    toggleMedia(variantData.id);
    
    // Update inventory status
    updateInventoryStatus(variantData);
  }

  // Update inventory status based on variant id
  function updateInventoryStatus(variantData) {
    const variantId = variantData.id;
    if (!variantId) return;
    
    // Hide all status messages
    const allStatusMessages = document.querySelectorAll('.product-block--inventory .inventory-status-p');
    allStatusMessages.forEach(msg => msg.style.display = 'none');
    // Show the status messages for the selected variant
    const variantMessage = document.querySelector(`.product-block--inventory p[data-variant-id="${variantId}"]`);
    if(variantMessage) {
      variantMessage.style.display = 'block';
    }
  }

  // produc tvariant image
  function toggleMedia(variantId) {
    if(!variantId) return;
    const mediaIds = document.querySelector(`[data-variant-id="${variantId}"]`).getAttribute('data-media-ids');
    const productMediaIds = [];
    const productData = JSON.parse(document.querySelector('#product-data').getAttribute('data-product') || '[]');
    if(productData.media.length) {
      productData.media.map(function (i) {
        productMediaIds.push(String(i.id));
      })
    }
    // 判断productMediaIds和mediaIds有没有交集
    const hasIntersection = productMediaIds.some(id => mediaIds.includes(id));
    if(!hasIntersection) {
      document.querySelectorAll('.product-media-collage__item').forEach(function (i) {
        i.removeAttribute('hidden')
      })
      return;
    }
    if(mediaIds) {
      // pc
      document.querySelectorAll('.product-media-collage__item').forEach(function (i) {
        const mediaId = i.getAttribute('data-media-id');
        if(mediaIds.includes(mediaId)) {
          i.removeAttribute('hidden')
        }else{
          i.setAttribute('hidden', '')
        }
      })
      // mobile
      document.querySelectorAll('.mobile-only .slider__item').forEach(function (i) {
        const mediaId = i.getAttribute('data-media-id');
        if(mediaIds.includes(mediaId)) {
          i.removeAttribute('hidden')
        }else{
          i.setAttribute('hidden', '')
        }
      })
    }else{
      document.querySelectorAll('.product-media-collage__item').forEach(function (i) {
        i.removeAttribute('hidden')
      })
      document.querySelectorAll('.mobile-only .slider__item').forEach(function (i) {
        i.removeAttribute('hidden')
      })
    }
  }
  // product page init
  if(document.querySelector('#product-data')) {
    const firstVariantId = document.querySelector('#product-data').getAttribute('data-first-variant-id');
    // page load init
    toggleMedia(firstVariantId);
  }

  // product fit last tips
  function updateLastFilledSegment() {
    // Remove triangle class from all segments first
    document.querySelectorAll('.segment-bar__segment--filled').forEach(segment => {
      segment.classList.remove('segment-bar__segment--filled-last');
    });
    
    // Get all filled segments and add the class to the last one
    const filledSegments = document.querySelectorAll('.segment-bar__segment--filled');
    if (filledSegments.length > 0) {
      filledSegments[filledSegments.length - 1].classList.add('segment-bar__segment--filled-last');
    }
  }

  updateLastFilledSegment();

// nav hide tmenu_item_link
(function(){
  // Create a function to handle the elements
  const hideWatermarkElements = () => {
    document.querySelectorAll('.tmenu-watermark.tmenu_item_link').forEach(element => {
      element.style.display = 'none';
    });
  };

  // Create a MutationObserver instance
  const observer = new MutationObserver((mutations) => {
    hideWatermarkElements();
  });

  // Start observing the document with the configured parameters
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Initial check
  hideWatermarkElements();
})();

(function() {
    let checkCount = 0;
    const maxChecks = 5;

    const timer = setInterval(() => {
      checkCount++;
      const xcottonWrapper = document.getElementById('xcottonWrapper');
      const checkoutContainer = document.querySelector('.checkout-buttons');

      if (xcottonWrapper) {
        clearInterval(timer);
        return;
      }

      if (checkCount >= maxChecks) {
        clearInterval(timer);
        
        if (checkoutContainer) {
          const defaultText = checkoutContainer.getAttribute('data-checkout-text');
          const checkoutBtn = checkoutContainer.querySelector('button[name="checkout"], a[href="/checkout"]');

          if (checkoutBtn && defaultText) {
            checkoutBtn.textContent = defaultText;
          }
        }
      }
    }, 300);
  })();
});

// 商品列表下拉加载更多
document.addEventListener('DOMContentLoaded', function() {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  // 获取当前页码和总页数
  let currentPage = parseInt(productGrid.dataset.currentPage) || 1;
  const totalPages = parseInt(productGrid.dataset.totalPages) || 1;

  // 如果只有一页，不需要加载更多功能
  if (totalPages <= 1) {
    const loadingMore = document.querySelector('.loading-more');
    if (loadingMore) {
      loadingMore.style.display = 'none';
    }
    return;
  }

  const loadingMore = document.querySelector('.loading-more');
  let isLoading = false;
  let hasMore = true;

  // 获取下一页URL
  function getNextPageUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('page', currentPage + 1);
    return url.toString();
  }

  // 加载更多商品
  async function loadMoreProducts() {
    if (isLoading || !hasMore) return;

    isLoading = true;
    loadingMore.classList.add('active');

    try {
      const response = await fetch(getNextPageUrl());
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      
      // 获取新的商品元素
      const newGrid = doc.getElementById('product-grid');
      if (newGrid && newGrid.children.length > 0) {
        // 将新商品添加到现有列表中
        Array.from(newGrid.children).forEach(child => {
          productGrid.appendChild(child.cloneNode(true));
        });
        
        currentPage++;
        
        // 检查是否还有更多页
        if (currentPage >= totalPages) {
          hasMore = false;
          loadingMore.innerHTML = '<div class="loading-more__text">--end--</div>';
        }
      } else {
        throw new Error('No products found');
      }
    } catch (error) {
      console.error('error:', error);
      hasMore = false; // 加载失败时停止后续加载
      loadingMore.innerHTML = '<div class="loading-more__text">Loading failed, please refresh to try again!</div>';
    } finally {
      isLoading = false;
      if (hasMore) {
        loadingMore.classList.remove('active');
      }
    }
  }

  // 监听滚动事件
  let scrollTimeout;
  window.addEventListener('scroll', function() {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    scrollTimeout = setTimeout(function() {
      const productGridRect = productGrid.getBoundingClientRect();
      const scrollPosition = window.innerHeight - productGridRect.bottom;

      // 当商品容器底部距离视窗底部小于300px时触发加载
      if (scrollPosition > -300) {
        loadMoreProducts();
      }
    }, 100);
  });
});